import React, { useState, useEffect } from 'react';
import { Form, Input } from 'antd';
import { CommonButton, FormWrapper } from '../../../../components';
import { createRefundService } from '../../../../services/index.service';
import { modalNotification } from '../../../../utils';
import { useSelector } from 'react-redux';
import RefundPaymentMethodSelection from './RefundPaymentMethodSelection';
import RefundSPPayDeduction from './RefundSPPayDeduction';
import { validation } from './validation';
import i18next from 'i18next';
import { 
  PAYMENT_TYPES, 
  REFUND_STEPS, 
  VALIDATION_LIMITS,
  COLORS,
  commonButtonStyle,
  inputLargeStyle,
  inputMediumStyle,
  textAreaStyle,
  formLabelStyle,
  formItemProps,
  footerContainerStyle
} from './constants';

const { TextArea } = Input;

export default function Refund({
  rowData,
  getData,
  setIsOpenModal,
  bookingPaymentId,
  paymentMode,
  onPaymentTypeChange,
  onStepChange,
}) {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(REFUND_STEPS.PAYMENT_METHOD); // paymentMethod, form, spPayDeduction
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [formData, setFormData] = useState({});
  const [shouldDeductSPPay, setShouldDeductSPPay] = useState(null);
  const isNCAUser = useSelector(
    (state) => state?.auth?.userData?.Role?.type === 'nca'
  );

// Get initial values for refund amount
const getInitialValues = () => {
  // Only use formData if we're coming back from SP Pay Deduction step
  if (Object.keys(formData).length > 0 && formData.refundAmount && currentStep === REFUND_STEPS.FORM) {
    return {
      refundAmount: formData.refundAmount || '',
      paymentType: selectedPaymentType || formData.paymentType || '',
      bankName: formData.bankName || '',
      checkNumber: formData.checkNumber || '',
      reason: formData.reason || '',
    };
  }
  
  // Default empty values
  return {
    refundAmount: '',
    paymentType: selectedPaymentType || '',
    bankName: '',
    checkNumber: '',
    reason: '',
  };
};

  // const formikRef = React.useRef(null);
  // useEffect(() => {
  //   if (selectedPaymentType && formikRef.current) {
  //     formikRef.current.setFieldValue('paymentType', selectedPaymentType, false);
  //   }
  // }, [selectedPaymentType]);

  const handlePaymentMethodSelect = (paymentType) => {
    setSelectedPaymentType(paymentType);
    setCurrentStep(REFUND_STEPS.FORM);
    if (onPaymentTypeChange) {
      onPaymentTypeChange(paymentType);
    }
    if (onStepChange) {
      setCurrentStep(REFUND_STEPS.FORM);
    }
  };

  const handleFormSubmit = (values) => {
    setFormData(values);
    if (isNCAUser) {
      // Skip SP pay deduction for NCA users - directly call API
      handleFinalSubmit(values, null);
    } else {
      // Show SP Pay Deduction modal for non-NCA users
      setCurrentStep(REFUND_STEPS.SP_PAY_DEDUCTION);
      if (onStepChange) {
        setCurrentStep(REFUND_STEPS.SP_PAY_DEDUCTION);
      }
    }
  };

  const handleSPPayDeduction = (value) => {
    setShouldDeductSPPay(value);
    // After SP Pay Deduction modal submits, call the refund API
    handleFinalSubmit(formData, value);
  };

  const handleFinalSubmit = async (values, spPayValue) => {
    setLoading(true);
    try {
      const bodyData = {
        bookingId: rowData?.id,
        bookingPaymentId: bookingPaymentId,
        refundAmount: parseFloat(values.refundAmount),
        paymentType: selectedPaymentType,
        bankName: values.bankName || null,
        checkNumber: values.checkNumber || null,
        reason: values.reason || '',
        shouldDeductSPPay: spPayValue !== null ? spPayValue : shouldDeductSPPay,
      };

      const response = await createRefundService({ bodyData });
      
      if (response?.success) {
        modalNotification({
          type: 'success',
          message: response?.message || i18next.t('text.report.refundProcessedSuccessfully'),
        });
        setIsOpenModal('');
        if (getData) {
          getData();
        }
      } else {
        modalNotification({
          type: 'error',
          message: response?.message || i18next.t('text.report.refundFailedToProcess'),
        });
      }
    } catch (error) {
      modalNotification({
        type: 'error',
        message: error?.message || i18next.t('text.report.refundErrorOccurred'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({});
    setSelectedPaymentType(null);
    setShouldDeductSPPay(null);
    setCurrentStep(REFUND_STEPS.PAYMENT_METHOD);
    setIsOpenModal('');
    if (onPaymentTypeChange) {
      onPaymentTypeChange(null);
    }
    if (onStepChange) {
      onStepChange(null);
    }
  };

  const handleBack = () => {
    if (currentStep === REFUND_STEPS.FORM) {
      // Clear form data when going back from form to payment method selection
      setFormData({});
      setCurrentStep(REFUND_STEPS.PAYMENT_METHOD);
      setSelectedPaymentType(null);
      if (onPaymentTypeChange) {
        onPaymentTypeChange(null);
      }
      if (onStepChange) {
        onStepChange(REFUND_STEPS.PAYMENT_METHOD);
      }
    } else if (currentStep === REFUND_STEPS.SP_PAY_DEDUCTION) {
      // Keep form data when going back from SP Pay step to form step
      setCurrentStep(REFUND_STEPS.FORM);
      if (onStepChange) {
        setCurrentStep(REFUND_STEPS.FORM);
      }
      // formData is preserved, so form will be prefilled
    }
  };

  const isCardPayment = selectedPaymentType === PAYMENT_TYPES.CARD;
  const isCheckPayment = selectedPaymentType === PAYMENT_TYPES.CHECK;
  const isFinancingPayment = selectedPaymentType === PAYMENT_TYPES.FINANCE;
  const isCashPayment = selectedPaymentType === PAYMENT_TYPES.CASH;

// Step 1: Payment Method Selection
if (currentStep === REFUND_STEPS.PAYMENT_METHOD) {
  return (
    <RefundPaymentMethodSelection
      onSelect={handlePaymentMethodSelect}
      onCancel={handleCancel}
    />
  );
}

// Step 2: SP Pay Deduction (only for non-NCA users)
if (currentStep === REFUND_STEPS.SP_PAY_DEDUCTION) {
  return (
    <RefundSPPayDeduction
      onSubmit={handleSPPayDeduction}
      onBack={handleBack}
      onCancel={handleCancel}
      loading={loading}
    />
  );
}

// Step 3: Refund Form
return (
  <div style={{ padding: '5px 24px', width: '100%' }}>
    <style>
      {`
        .refund-form .ant-form-item-label > label.ant-form-item-required::before {
          display: none !important;
        }
      `}
    </style>
    <FormWrapper
      onSubmit={handleFormSubmit}
      // validation={() => validation(rowData?.customerPaid, selectedPaymentType)}
      validation={() => {
        let maxRefundAmount = rowData?.customerPaid;
        
        if (bookingPaymentId && rowData?.BookingPayments?.length > 0) {
          const specificPayment = rowData.BookingPayments.find(
            (payment) => payment.id === bookingPaymentId
          );
          
          if (specificPayment && rowData?.BookingDetail?.paymentStatus === 'pending' && rowData?.BookingDetail?.isSplitPayment) {
            const totalAmount = rowData?.BookingPayments?.reduce((sum, payment) => {
                if (payment.paymentMode !== 'request') {
                    return sum + parseFloat(payment.paidAmount);
                }
                return sum;
            }, 0);
            maxRefundAmount = totalAmount ? totalAmount : specificPayment.paidAmount;
          }
        }
        return validation(maxRefundAmount, selectedPaymentType);
      }}
      layout="vertical"
      className="refund-form"
      initialValues={getInitialValues()}
      data={formData}
      enableReinitialize={true}
    >
      {(formikProps) => {
        // Sync paymentType to form values when selectedPaymentType changes
        if (selectedPaymentType && formikProps.values.paymentType !== selectedPaymentType) {
          formikProps.setFieldValue('paymentType', selectedPaymentType, false);
        }

        return (
        <>
          {/* Hidden field for paymentType */}
          <input type="hidden" name="paymentType" value={selectedPaymentType || ''} />
          <Form.Item
            required={false}
            label={
              <div style={{ fontSize: '15px', fontWeight: 500, color: '#333', display: 'block' }}>
                {i18next.t('text.report.refundAmountToRefund')} <span style={{ color: COLORS.REQUIRED_ASTERISK }}>*</span>
              </div>
            }
            name="refundAmount"
            validateStatus={formikProps.errors.refundAmount ? 'error' : ''}
            help={formikProps.errors.refundAmount || null}
            style={{ marginBottom: '12px' }}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Input
              type="number"
              step="1"
              placeholder="0.00"
              name="refundAmount"
              value={formikProps.values.refundAmount === undefined || formikProps.values.refundAmount === null ? '' : formikProps.values.refundAmount}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : e.target.value;
                formikProps.setFieldValue('refundAmount', val);
                if (val === '') {
                  formikProps.setFieldError('refundAmount', i18next.t('validation.report.refundAmountRequired'));
                } else {
                  formikProps.setFieldError('refundAmount', undefined);
                }
              }}
              onBlur={(e) => {
                formikProps.handleBlur(e);
                if (!e.target.value || e.target.value.trim() === '') {
                  formikProps.setFieldTouched('refundAmount', true);
                }
              }}
              size="large"
              style={inputLargeStyle}
            />
          </Form.Item>

          {/* Bank Name - Only for Check and Finance */}
          {(isCheckPayment || isFinancingPayment) && (
            <Form.Item
              required={false}
              label={
                <div style={{ fontSize: '15px', fontWeight: 500, color: '#333', display: 'block' }}>
                  {i18next.t('text.report.refundBankName')} <span style={{ color: COLORS.REQUIRED_ASTERISK }}>*</span>
                </div>
              }
              name="bankName"
              validateStatus={formikProps.touched.bankName && formikProps.errors.bankName ? 'error' : ''}
              help={formikProps.touched.bankName && formikProps.errors.bankName}
              style={{ marginBottom: '12px' }}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input 
                placeholder={i18next.t('text.report.refundEnterBankName')}
                value={formikProps.values.bankName}
                onChange={(e) => formikProps.setFieldValue('bankName', e.target.value)}
                onBlur={formikProps.handleBlur}
                size="large"
                style={inputMediumStyle}
              />
            </Form.Item>
          )}

          {/* Check Number / Financing Confirmation Number - Only for Check and Finance */}
          {(isCheckPayment || isFinancingPayment) && (
            <Form.Item
              required={false}
              label={
                <div style={{ fontSize: '15px', fontWeight: 500, color: '#333', display: 'block' }}>
                  {isFinancingPayment 
                    ? i18next.t('text.report.refundFinancingConfirmationNumber')
                    : i18next.t('text.report.refundCheckNumber')
                  } <span style={{ color: COLORS.REQUIRED_ASTERISK }}>*</span>
                </div>
              }
              name="checkNumber"
              validateStatus={formikProps.touched.checkNumber && formikProps.errors.checkNumber ? 'error' : ''}
              help={formikProps.touched.checkNumber && formikProps.errors.checkNumber || null}
              style={{ marginBottom: '12px' }}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input 
                placeholder={
                  isFinancingPayment 
                    ? i18next.t('text.report.refundEnterFinancingConfirmationNumber')
                    : i18next.t('text.report.refundEnterCheckNumber')
                }
                value={formikProps.values.checkNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  // Check if value contains special characters
                  const hasSpecialChars = /[^A-Za-z0-9]/.test(value);
                  
                  if (hasSpecialChars) {
                    // Set field as touched and set validation error for special characters
                    formikProps.setFieldTouched('checkNumber', true, false);
                    formikProps.setFieldError('checkNumber', i18next.t('validation.report.specialCharacterNotAllowed'));
                    // Filter the value to prevent special characters from being entered
                    const filteredValue = value.replace(/[^A-Za-z0-9]/g, '');
                    formikProps.setFieldValue('checkNumber', filteredValue, false);
                  } else {
                    // Only clear special character error if it exists, don't clear other validation errors
                    const currentError = formikProps.errors.checkNumber;
                    if (currentError === i18next.t('validation.report.specialCharacterNotAllowed')) {
                      formikProps.setFieldError('checkNumber', undefined);
                    }
                    // Let formik handle the change normally so other validations can run
                    formikProps.setFieldValue('checkNumber', value);
                  }
                }}
                onBlur={(e) => {
                  formikProps.handleBlur(e);
                  // Re-validate on blur - only check for special characters if value exists
                  const value = formikProps.values.checkNumber || '';
                  if (value && /[^A-Za-z0-9]/.test(value)) {
                    formikProps.setFieldTouched('checkNumber', true, false);
                    formikProps.setFieldError('checkNumber', i18next.t('validation.report.specialCharacterNotAllowed'));
                  }
                }}
                maxLength={isFinancingPayment ? 30 : 30}
                size="large"
                style={inputMediumStyle}
              />
            </Form.Item>
          )}

          <Form.Item
            required={false}
            label={
              <div style={{ fontSize: '15px', fontWeight: 500, color: '#333', display: 'block' }}>
                {i18next.t('text.report.refundReason')} <span style={{ color: COLORS.REQUIRED_ASTERISK }}>*</span>
              </div>
            }
            name="reason"
            validateStatus={formikProps.touched.reason && formikProps.errors.reason ? 'error' : ''}
            help={formikProps.touched.reason && formikProps.errors.reason}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <TextArea
              rows={4}
              placeholder={i18next.t('text.report.refundEnterReason')}
              value={formikProps.values.reason}
              onChange={(e) => formikProps.setFieldValue('reason', e.target.value)}
              onBlur={formikProps.handleBlur}
              style={textAreaStyle}
            />
          </Form.Item>

          <Form.Item className="mb-0" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
            <div style={footerContainerStyle}>
              <CommonButton
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
                disabled={loading}
                style={commonButtonStyle}
              >
                Back
              </CommonButton>
              <div 
                style={{ 
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '14px',
                  alignItems: 'center'
                }}
              >
                <CommonButton
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  style={commonButtonStyle}
                >
                  Cancel
                </CommonButton>
                <CommonButton
                  type="submit"
                  className="btn btn-primary"
                  loading={loading}
                  style={commonButtonStyle}
                >
                  Next
                </CommonButton>
              </div>
            </div>
          </Form.Item>
        </>
      )}}
    </FormWrapper>
  </div>
);
}